import { useState } from 'react';
import { IconArrowLeft, IconLogout2 } from '@tabler/icons-react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Button,
  Divider,
  Group,
  Image,
  NavLink,
  rem,
  ScrollArea,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { useMyDelegations } from '@/hooks/useUserControl';
import { getLogoSvg } from '../Logo/LogoSvg';
import { ThemeSchemeToggle } from '../ThemeSchemeToggle/ThemeSchemeToggle';
import { UserMenu } from '../UserMenu/UserMenu';
import { navigationOrganizerLinks } from './navigationLinks';

const LayoutDashboardOrganizer = () => {
  const eventId = useParams().eventId || '';

  const [opened, { toggle, close }] = useDisclosure();
  const { colorScheme } = useMantineColorScheme();

  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  // Função na "Equipe dos eventos" deste evento (Staff ou Financeiro), se a pessoa foi delegada
  const { data: myDelegations } = useMyDelegations(!!currentUser && !isAdmin);
  const staffRole = myDelegations?.find((delegation) => delegation.eventId === eventId)?.role;
  const logoSvg = getLogoSvg();
  const [activeItem, setActiveItem] = useState<{ menuId: number | null; submenuId: number | null }>(
    {
      menuId: null,
      submenuId: null,
    }
  );
  return (
    <AppShell
      header={{ height: 70 }}
      navbar={{
        width: { base: 300 },
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      pt="md"
    >
      <AppShell.Header
        bg={colorScheme === 'dark' ? '#191919' : 'white'}
        style={{ border: 'none', boxShadow: 'none' }}
      >
        <Group h="100%" px="md" justify="space-between" align="center">
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Link to="/dashboard/evento/tabela" style={{ textDecoration: 'none' }}>
            <Image w={185} src={logoSvg} alt="Brasil Arena" />
          </Link>

          <Group gap="xs">
            <ThemeSchemeToggle size={38} />
            <UserMenu size={38} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        bg={colorScheme === 'dark' ? '#191919' : 'white'}
        style={{
          borderTop: `1px solid ${colorScheme === 'dark' ? '#2e2e2e' : '#e9e9e9'}`,
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          boxShadow: 'none',
          justifyContent: 'space-between',
        }}
      >
        <ScrollArea type="never">
          <Button
            mb="lg"
            ml={5}
            fz={17}
            onClick={() => navigate('/dashboard/evento/tabela')}
            component="a"
            variant="subtle"
            color="principal"
            leftSection={<IconArrowLeft size={24} />}
          >
            Voltar para Campeonatos
          </Button>

          <Divider color={colorScheme === 'dark' ? '#2e2e2e' : '#e9e9e9'} />

          {navigationOrganizerLinks
            .filter((menu) => !staffRole || !menu.hiddenForStaffRoles?.includes(staffRole))
            .map((menu) => (
              <NavLink
                key={menu.id}
                p={10}
                mb={4}
                label={menu.label}
                color={colorScheme === 'dark' ? '#f1f3f5' : '#3c3d3d'}
                leftSection={<menu.icon style={{ width: rem(19), height: rem(19) }} />}
                active={menu.id === activeItem.menuId}
                onClick={() => {
                  setActiveItem({ menuId: menu.id, submenuId: null });
                  close();
                }}
                autoContrast
                component={Link}
                to={menu.href.replace(':eventId', eventId)}
                sx={(theme) => ({
                  position: 'relative',
                  textDecoration: 'none',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: theme.spacing.lg,
                  paddingRight: theme.spacing.lg,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  minHeight: '40px',

                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',

                    height: '4px',
                    borderRadius: '4px',
                    backgroundColor: theme.colors.principal[5],
                    bottom: -5,
                    left: '0',
                    transformOrigin: 'right',
                    transform: 'scaleX(0)',
                    transition: 'transform 0.3s ease-in-out',
                  },
                  '&:hover::before': {
                    transformOrigin: 'left',
                    transform: 'scaleX(1)',
                  },
                })}
              />
            ))}
        </ScrollArea>

        <Button
          m={20}
          fz={17}
          onClick={logout}
          component="a"
          color="gray"
          variant="subtle"
          href="/"
          leftSection={<IconLogout2 size={24} />}
        >
          SAIR
        </Button>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
};

export default LayoutDashboardOrganizer;
